/**
 * Explains the company customise-in-builder flow: criteria → events → save.
 * Nothing is persisted until the user saves on the final step.
 */
import React from "react";
import { translateUi } from "../i18n/index";
import { cb, cbCn } from "./cycleBuilderTheme";

const STEPS = [
  { id: "criteria", label: translateUi("Customise criteria") },
  { id: "events", label: translateUi("Schedule assessment events") },
  { id: "save", label: translateUi("Save company cycle") },
] as const;

export type CompanyCycleWorkflowStep = "criteria" | "events";

export function CompanyCycleWorkflowCallout({
  activeStep,
  isDarkMode = false,
  criteriaCount,
  cycleTitle,
}: {
  activeStep: CompanyCycleWorkflowStep;
  isDarkMode?: boolean;
  criteriaCount?: number;
  cycleTitle?: string;
}) {
  const activeIndex = activeStep === "criteria" ? 0 : 1;

  return (
    <div
      className={cbCn(
        "rounded-xl border px-4 py-3",
        isDarkMode ? "border-indigo-500/35 bg-indigo-950/30" : "border-indigo-200 bg-indigo-50/80"
      )}
    >
      <p className={cbCn("text-sm font-semibold", isDarkMode ? "text-indigo-100" : "text-indigo-950")}>
        {translateUi("Creating your company cycle")}
      </p>
      {cycleTitle?.trim() ? (
        <p className={cbCn("mt-0.5 text-xs", isDarkMode ? "text-indigo-200/90" : "text-indigo-900/80")}>
          {cycleTitle.trim()}
          {typeof criteriaCount === "number"
            ? ` · ${criteriaCount} ${criteriaCount === 1 ? translateUi("criterion") : translateUi("criteria")}`
            : null}
        </p>
      ) : null}
      <ol className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-2">
        {STEPS.map((step, index) => {
          const isActive = index === activeIndex;
          const isDone = index < activeIndex;
          return (
            <li
              key={step.id}
              className={cbCn(
                "flex min-w-0 flex-1 items-start gap-2 rounded-lg px-2.5 py-2 text-xs",
                isActive
                  ? isDarkMode
                    ? "bg-indigo-500/20 ring-1 ring-indigo-400/50"
                    : "bg-white ring-1 ring-indigo-300"
                  : isDone
                    ? isDarkMode
                      ? "bg-slate-800/60 text-slate-300"
                      : "bg-white/70 text-slate-600"
                    : isDarkMode
                      ? "text-slate-400"
                      : "text-slate-500"
              )}
            >
              <span
                className={cbCn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums",
                  isActive
                    ? "bg-indigo-600 text-white"
                    : isDone
                      ? isDarkMode
                        ? "bg-emerald-900/80 text-emerald-200"
                        : "bg-emerald-100 text-emerald-800"
                      : isDarkMode
                        ? "bg-slate-700 text-slate-300"
                        : "bg-slate-200 text-slate-600"
                )}
                aria-hidden
              >
                {index + 1}
              </span>
              <span className={cbCn("min-w-0 font-medium leading-snug", isActive && (isDarkMode ? "text-indigo-50" : "text-indigo-950"))}>
                {step.label}
                {step.id === "save" ? (
                  <span className={cbCn("mt-0.5 block font-normal", isDarkMode ? "text-indigo-200/80" : "text-indigo-800/80")}>
                    {translateUi("Adds a draft — release to managers when ready")}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      <p className={cbCn("mt-3 text-xs", isDarkMode ? "text-indigo-100/85" : "text-indigo-900/85")}>
        {activeStep === "criteria"
          ? translateUi(
              "Your changes are kept as you work — nothing is saved to your company cycles until you finish step 2 and click Save company cycle."
            )
          : translateUi(
              "Your criteria from step 1 are included below. When you save, this becomes a draft company cycle you can review and release to managers."
            )}
      </p>
    </div>
  );
}
